/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { makeStyles, Theme } from '@mui/material/styles';

import {FormControlLabel, Checkbox as MuiCheckbox, CheckboxProps as MuiCheckboxProps } from '@mui/material';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */
import { IFieldInputProps } from '@ngt/forms-core';

import { FormControl, FormControlLabelProps, FormHelperText, FormControlProps } from '@mui/material';
import { IAdditionalFormInputProps } from './Input';
import useErrorStyling, { ErrorClassMapping, ErrorStylingType } from '../../../hooks/form/useErrorStyling';
import { Fragment, ReactNode } from 'react';
import { FormError } from '../../../errors';
import FormErrorType from '@/types/form/FormErrorType';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

type CheckboxsProps = Partial<Omit<MuiCheckboxProps, 'type'>> & IFieldInputProps<boolean, any, FormError, boolean> & IAdditionalFormInputProps

export interface ICheckboxProps extends CheckboxsProps {
    FormControlProps?: FormControlProps,
    FormControlLabelProps?: Omit<FormControlLabelProps, 'control' | 'label'> & { label?: FormControlLabelProps['label']};
    helperText?: ReactNode;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const Checkbox: React.FunctionComponent<ICheckboxProps> = ({
    FieldInputProps: { state: { propertyPath, value }, actions: { update, blur, focus, touch } },
    label,
    onChange,
    FormControlProps,
    FormControlLabelProps,
    inputProps,
    disabled,
    hasErrors,
    errors,
    errorType,
    helperText,
    ...rest
}) => {
    const onChangeCombined = React.useCallback((event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        if (onChange) {
            onChange(event, checked);
        }

        update(checked, true, true, true);
    }, [onChange, update]);

    const onFocusCombined = React.useCallback((event: React.FocusEvent<HTMLInputElement>) => {
        if (inputProps?.onFocus) {
            inputProps.onFocus(event);
        }

        focus();
    }, [inputProps?.onFocus, focus]);

    const onBlurCombined = React.useCallback((event: React.FocusEvent<HTMLInputElement>) => {
        if (inputProps?.onBlur) {
            inputProps.onBlur(event);
        }

        blur();
        touch();
    }, [inputProps?.onBlur, blur, touch]);


    const errorStyling = useErrorStyling(errorType, ErrorStylingType.Input);

    return (
        <FormControl
            key={`${propertyPath}-control`}
            {...FormControlProps}
            error={hasErrors}
            disabled={disabled}
            fullWidth={FormControlProps?.fullWidth === undefined ? true : FormControlProps?.fullWidth}
            sx={{...FormControlProps?.sx, ...errorStyling}}
        >
            <FormControlLabel
                key={`${propertyPath}-label`}
                {...FormControlLabelProps}
                control={
                    <MuiCheckbox
                        key={`${propertyPath}-checkbox`}
                        {...rest as any}
                        inputProps={{
                            ...inputProps,
                            onBlur: onBlurCombined,
                            onFocus: onFocusCombined
                        }}
                        checked={value ?? false}
                        onChange={onChangeCombined}
                        disabled={disabled}
                        color={rest?.color ? rest?.color : 'primary'}
                    />
                }
                label={<Fragment key={`${propertyPath}-label`}>{ FormControlLabelProps?.label ?? label }</Fragment>}
            />
            {
                (hasErrors || helperText) && (
                    <FormHelperText>
                        {helperText && (<span className={ErrorClassMapping[FormErrorType.HelperText]}>{helperText}</span>)} 
                        {errors && errors.length > 0 && errors?.map((error, i) => (<span className={ErrorClassMapping[error.type!]} key={i}>{error.message}</span>))}
                    </FormHelperText>
                )
            }
        </FormControl>
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default Checkbox;
